// Payload for a proposed side-effecting action that paused the turn. The action
// only runs when the user approves it; the model is never in the send path.
// `actionKind` selects the preview shape. Model-generated fields (subject, body,
// title) are untrusted and must render as text, never markup. `status` carries
// the terminal state when the backend re-emits a resolved action, so the card
// reflects the outcome without re-running the request.

// The backend can emit any lifecycle state, including non-interactive ones: an
// auto-approved action arrives already "queued".
export type ConfirmActionStatus = "pending" | "queued" | "executing" | "executed" | "rejected" | "failed" | "blocked";

// When `draftId` is set, to/subject/body come from a saved Gmail draft and the
// backend sends the draft as-is, so the card renders read-only.
export interface EmailPreview {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
  draftId?: string;
}

// `addMeet` and `recurrence` are always present on create/update previews
// (`recurrence` empty when unset); `calendarId` appears only for a non-primary
// calendar.
export interface CalendarPreview {
  title: string;
  start?: string;
  end?: string;
  allDay?: boolean;
  attendees?: string[];
  location?: string;
  description?: string;
  eventId?: string;
  addMeet?: boolean;
  recurrence?: string[];
  calendarId?: string;
}

export type RsvpResponse = "accepted" | "declined" | "tentative";

// The other CalendarPreview fields arrive empty for an RSVP; only `eventId` and
// `response` carry meaning.
export interface RsvpPreview extends CalendarPreview {
  eventId: string;
  response: RsvpResponse;
}

export interface SendEmailAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "send_email";
  preview: EmailPreview;
  status: ConfirmActionStatus;
}

export interface CalendarAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "calendar_create" | "calendar_update" | "calendar_delete";
  preview: CalendarPreview;
  notifyAttendeesDefault: boolean;
  status: ConfirmActionStatus;
}

export interface RsvpAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "calendar_rsvp";
  preview: RsvpPreview;
  notifyAttendeesDefault: boolean;
  status: ConfirmActionStatus;
}

// `calendarId` is the source calendar (absent when primary); the remaining
// CalendarPreview fields arrive empty for a move.
export interface MovePreview extends CalendarPreview {
  eventId: string;
  destinationCalendarId: string;
}

export interface MoveAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "calendar_move";
  preview: MovePreview;
  notifyAttendeesDefault: boolean;
  status: ConfirmActionStatus;
}

// All fields are untrusted and must render as text, never markup. `from` is the
// raw RFC 5322 header.
interface GmailTrashMessage {
  id: string;
  from: string;
  subject: string;
  date: string;
}

// `messages` is capped backend-side, so `count` can exceed its length. It is
// absent on cards persisted before per-email summaries existed.
export interface GmailTrashPreview {
  untrash: boolean;
  messageIds: string[];
  count: number;
  messages: GmailTrashMessage[];
}

export interface GmailTrashAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "gmail_trash";
  preview: GmailTrashPreview;
  status: ConfirmActionStatus;
}

// update/delete take any draft id, including one the user wrote by hand, so the
// card must show what is overwritten or lost. `current*` snapshot the draft at
// propose time.
export interface GmailDraftUpdatePreview {
  draftId: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  currentSubject: string;
  currentTo: string[];
}

export interface GmailDraftDeletePreview {
  draftId: string;
  currentSubject: string;
  currentTo: string[];
}

export interface GmailDraftUpdateAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "gmail_draft_update";
  preview: GmailDraftUpdatePreview;
  status: ConfirmActionStatus;
}

export interface GmailDraftDeleteAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "gmail_draft_delete";
  preview: GmailDraftDeletePreview;
  status: ConfirmActionStatus;
}

// Slack writes always go out as the org's Chief bot, so the only editable field
// is the message text; channel/recipient/thread are fixed by the proposal.
// `channelName` and `recipientName` are display-resolved (no leading '#').
// `mentions` maps bare Slack user ids appearing as `<@UID>` tokens in `text`
// to display names; ids the backend couldn't resolve are absent.
export interface SlackSendMessagePreview {
  channelId: string;
  channelName: string;
  text: string;
  mentions?: Record<string, string>;
}

export interface SlackSendDmPreview {
  recipientId: string;
  recipientName: string;
  text: string;
  mentions?: Record<string, string>;
}

export interface SlackReplyPreview {
  channelId: string;
  channelName: string;
  threadTs: string;
  text: string;
  mentions?: Record<string, string>;
}

// `emoji` is the bare emoji name without colons; `messageText` is a snippet of
// the message being reacted to.
export interface SlackReactionPreview {
  channelId: string;
  channelName: string;
  messageTs: string;
  emoji: string;
  messageText?: string;
}

export interface SlackSendMessageAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "send_message";
  preview: SlackSendMessagePreview;
  status: ConfirmActionStatus;
}

export interface SlackSendDmAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "send_dm";
  preview: SlackSendDmPreview;
  status: ConfirmActionStatus;
}

export interface SlackReplyAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "reply_thread";
  preview: SlackReplyPreview;
  status: ConfirmActionStatus;
}

export interface SlackReactionAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "add_reaction";
  preview: SlackReactionPreview;
  status: ConfirmActionStatus;
}

export type SlackTextAction = SlackSendMessageAction | SlackSendDmAction | SlackReplyAction;
export type SlackConfirmAction = SlackTextAction | SlackReactionAction;

// One content block in a Notion write; all fields are model-generated and untrusted.
export interface NotionBlockSpec {
  type: string;
  text: string;
  language?: string;
  checked?: boolean;
}

// `value` is raw model input, shaped per the `type` field.
export interface NotionPropertySpec {
  type: string;
  value: unknown;
}

// Wire field names are snake_case, unlike the rest of the preview. An empty
// new_str deletes the matched text.
export interface NotionEditSpec {
  old_str: string;
  new_str: string;
  replace_all?: boolean;
}

// Exactly one of parentPageId or parentDatabaseId is set; properties is
// populated only for a database parent. markdown is the page body; blocks is
// the legacy block-shaped body of actions proposed before the markdown path
// shipped.
export interface NotionCreatePagePreview {
  parentPageId?: string;
  parentDatabaseId?: string;
  parentTitle?: string;
  title?: string;
  iconEmoji?: string;
  markdown?: string;
  blocks?: NotionBlockSpec[];
  properties?: Record<string, NotionPropertySpec>;
}

// At most one content change is set: edits, replaceMarkdown (empty string
// clears the body), or blocks (append, placed by position or afterBlockId;
// with legacy replaceContent set they replace the body instead). archived
// moves the page to or out of the trash.
export interface NotionUpdatePagePreview {
  pageId: string;
  pageTitle?: string;
  edits?: NotionEditSpec[];
  replaceMarkdown?: string;
  blocks?: NotionBlockSpec[];
  position?: string;
  afterBlockId?: string;
  archived?: boolean;
  replaceContent?: boolean;
  allowDeletingContent?: boolean;
}

export interface NotionCreateDbRowPreview {
  databaseId: string;
  databaseTitle?: string;
  properties: Record<string, NotionPropertySpec>;
}

export interface NotionUpdateDbRowPreview {
  pageId: string;
  rowTitle?: string;
  properties: Record<string, NotionPropertySpec>;
}

// One column in a database schema; `options` carries the choices for
// select/multi_select/status columns and `relation_data_source_id` the target
// of a relation column — both absent for other types.
export interface NotionDatabaseColumnSpec {
  type: string;
  options?: string[];
  relation_data_source_id?: string;
}

// `properties` maps column name to its schema; `parentTitle` is the resolved
// Notion page name and may be empty, in which case `parentPageId` is shown.
// `rows` is the initial row data (column name → typed value), separate from the
// column schema and omitted when no rows are seeded.
export interface NotionCreateDatabasePreview {
  parentPageId: string;
  parentTitle?: string;
  title: string;
  properties: Record<string, NotionDatabaseColumnSpec>;
  rows?: Array<Record<string, NotionPropertySpec>>;
}

// Exactly one of pageId or discussionId is set.
export interface NotionAddCommentPreview {
  pageId?: string;
  discussionId?: string;
  pageTitle?: string;
  text: string;
}

// At least one of title, addColumns, or removeColumns is set. Removing a
// column discards its data.
export interface NotionUpdateDatabasePreview {
  databaseId: string;
  databaseTitle?: string;
  dataSourceId?: string;
  title?: string;
  addColumns?: Record<string, NotionDatabaseColumnSpec>;
  removeColumns?: string[];
}

// Exactly one of newParentPageId or newParentDataSourceId is set; a
// data-source destination makes the page a row of that database. parentTitle
// is resolved only for a page destination.
export interface NotionMovePagePreview {
  pageId: string;
  pageTitle?: string;
  newParentPageId?: string;
  newParentDataSourceId?: string;
  parentTitle?: string;
}

export interface NotionCreatePageAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_create_page";
  preview: NotionCreatePagePreview;
  status: ConfirmActionStatus;
}

export interface NotionUpdatePageAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_update_page";
  preview: NotionUpdatePagePreview;
  status: ConfirmActionStatus;
}

export interface NotionCreateDbRowAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_create_db_row";
  preview: NotionCreateDbRowPreview;
  status: ConfirmActionStatus;
}

export interface NotionUpdateDbRowAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_update_db_row";
  preview: NotionUpdateDbRowPreview;
  status: ConfirmActionStatus;
}

export interface NotionCreateDatabaseAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_create_database";
  preview: NotionCreateDatabasePreview;
  status: ConfirmActionStatus;
}

export interface NotionAddCommentAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_add_comment";
  preview: NotionAddCommentPreview;
  status: ConfirmActionStatus;
}

export interface NotionUpdateDatabaseAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_update_database";
  preview: NotionUpdateDatabasePreview;
  status: ConfirmActionStatus;
}

export interface NotionMovePageAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "notion_move_page";
  preview: NotionMovePagePreview;
  status: ConfirmActionStatus;
}

export type NotionConfirmAction =
  | NotionCreatePageAction
  | NotionUpdatePageAction
  | NotionCreateDbRowAction
  | NotionUpdateDbRowAction
  | NotionCreateDatabaseAction
  | NotionAddCommentAction
  | NotionUpdateDatabaseAction
  | NotionMovePageAction;

// `content` is the Markdown body; `parentFolderId` is absent when the Doc goes
// to the acting user's Drive root.
export interface GDriveCreateDocPreview {
  title: string;
  content?: string;
  parentFolderId?: string;
}

// `fileTitle` is the resolved Doc name and is absent when the lookup failed,
// leaving the raw id as the only handle to show.
export interface GDriveUpdateDocPreview {
  fileId: string;
  fileTitle?: string;
  mode: "replace" | "append";
  content?: string;
}

export interface GDriveCreateDocAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "gdrive_create_doc";
  preview: GDriveCreateDocPreview;
  status: ConfirmActionStatus;
}

export interface GDriveUpdateDocAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "gdrive_update_doc";
  preview: GDriveUpdateDocPreview;
  status: ConfirmActionStatus;
}

export type GDriveConfirmAction = GDriveCreateDocAction | GDriveUpdateDocAction;

// Linear previews carry display names only; the ids behind them were resolved
// at propose time, so a team or label the workspace doesn't have fails before
// the user clicks rather than after.
export interface LinearCreateIssuePreview {
  teamName: string;
  title: string;
  description?: string;
  assignee?: string;
  // Linear's 0-4 scale, where 0 is "no priority".
  priority?: number;
  project?: string;
  state?: string;
  labels?: string[];
}

// Only the fields being changed are present; an absent one is left untouched.
export interface LinearUpdateIssuePreview {
  identifier: string;
  issueTitle?: string;
  title?: string;
  description?: string;
  assignee?: string;
  state?: string;
  priority?: number;
  project?: string;
  addedLabels?: string[];
  removedLabels?: string[];
}

export interface LinearAddCommentPreview {
  identifier: string;
  issueTitle?: string;
  body: string;
}

export interface LinearCreateIssueAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "linear_create_issue";
  preview: LinearCreateIssuePreview;
  status: ConfirmActionStatus;
}

export interface LinearUpdateIssueAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "linear_update_issue";
  preview: LinearUpdateIssuePreview;
  status: ConfirmActionStatus;
}

export interface LinearAddCommentAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "linear_add_comment";
  preview: LinearAddCommentPreview;
  status: ConfirmActionStatus;
}

export type LinearConfirmAction = LinearCreateIssueAction | LinearUpdateIssueAction | LinearAddCommentAction;

// Assignees and labels were checked against the repository at propose time, so
// an unknown one fails before the user approves rather than after.
export interface GitHubCreateIssuePreview {
  repo: string;
  title: string;
  body?: string;
  assignees?: string[];
  labels?: string[];
}

// Labels and assignees arrive as add and remove sets rather than replacement
// lists, so a change to one cannot silently strip the rest.
export interface GitHubUpdateIssuePreview {
  repo: string;
  number: number;
  reference: string;
  issueTitle?: string;
  addLabels?: string[];
  removeLabels?: string[];
  addAssignees?: string[];
  removeAssignees?: string[];
  state?: string;
  stateReason?: string;
}

export interface GitHubAddCommentPreview {
  repo: string;
  number: number;
  reference: string;
  issueTitle?: string;
  body: string;
}

export interface GitHubCreateIssueAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "github_create_issue";
  preview: GitHubCreateIssuePreview;
  status: ConfirmActionStatus;
}

export interface GitHubUpdateIssueAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "github_update_issue";
  preview: GitHubUpdateIssuePreview;
  status: ConfirmActionStatus;
}

export interface GitHubAddCommentAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "github_add_comment";
  preview: GitHubAddCommentPreview;
  status: ConfirmActionStatus;
}

export type GitHubConfirmAction = GitHubCreateIssueAction | GitHubUpdateIssueAction | GitHubAddCommentAction;

// A non-read-only tool on a customer-connected MCP server. `args` are the
// model's arguments verbatim; the card shows them as JSON, since a server's
// tools have no shape the card could know ahead of time.
export interface MCPCallPreview {
  serverId: string;
  serverName: string;
  toolName: string;
  args: Record<string, unknown>;
}

// The kind follows the server's credential: a per-user OAuth token proposes
// mcp_call_user, an org-wide credential mcp_call_org.
export interface MCPCallAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "mcp_call_user" | "mcp_call_org";
  preview: MCPCallPreview;
  status: ConfirmActionStatus;
}

// Chief previews carry display names (`actionName`, `labelName`, `assetName`)
// resolved best-effort at propose time; cards fall back to the raw ids when one
// is absent.

// `hour` is always set; at most one of `weekday`/`monthDay` follows,
// matching the cadence (daily/weekly/monthly). `minutes` replaces all
// three: an interval action carries a wildcard hour and runs on the interval.
export interface ActionSchedulePreview {
  hour: string;
  weekday?: string;
  monthDay?: string;
  timezone?: string;
  minutes?: number;
}

type ActionTriggerKind = "new" | "all";

interface ActionTriggerPreview {
  kind: ActionTriggerKind;
  createdBy?: string[];
}

interface ActionEmailOutcomePreview {
  to: string[];
  subject?: string;
  includeDateInSubject?: boolean;
}

// Scope is server-authoritative and never editable from the card.
interface ActionScopePreview {
  assetIds?: string[];
  labelIds?: string[];
  conceptIds?: string[];
  viewIds?: string[];
}

// actionId/actionName appear only on update. At most one of schedule/trigger is set.
interface ActionConfigPreview {
  actionId?: string;
  actionName?: string;
  name: string;
  description?: string;
  prompt: string;
  schedule?: ActionSchedulePreview;
  trigger?: ActionTriggerPreview;
  scope?: ActionScopePreview;
  email?: ActionEmailOutcomePreview;
  mode?: string;
  enabled?: boolean;
  autoApprove?: string[];
}

export interface ActionSetEnabledPreview {
  actionId: string;
  actionName?: string;
  enabled: boolean;
}

export interface ActionRefPreview {
  actionId: string;
  actionName?: string;
}

export interface LabelCreatePreview {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
}

interface LabelAssetRef {
  id: string;
  name?: string;
}

export interface LabelAssetsPreview {
  labelId: string;
  labelName?: string;
  assets: LabelAssetRef[];
}

interface LabelDeletePreview {
  labelId: string;
  labelName?: string;
}

// `name`/`description` are the new values; unset fields are left unchanged.
export interface AssetUpdatePreview {
  assetId: string;
  assetName?: string;
  name?: string;
  description?: string;
}

interface AssetDeletePreview {
  assetId: string;
  assetName?: string;
}

export interface ActionCreateAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "action_create";
  preview: ActionConfigPreview;
  status: ConfirmActionStatus;
}

export interface ActionUpdateAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "action_update";
  preview: ActionConfigPreview;
  status: ConfirmActionStatus;
}

export interface ActionSetEnabledAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "action_set_enabled";
  preview: ActionSetEnabledPreview;
  status: ConfirmActionStatus;
}

export interface ActionDeleteAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "action_delete";
  preview: ActionRefPreview;
  status: ConfirmActionStatus;
}

export interface ActionDryRunAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "action_dry_run";
  preview: ActionRefPreview;
  status: ConfirmActionStatus;
}

export interface LabelCreateAction {
  type: "confirm_action";
  actionId: string;
  /** smart_tag_create shares the card: its instruction is the label's description. */
  actionKind: "label_create" | "smart_tag_create";
  preview: LabelCreatePreview;
  status: ConfirmActionStatus;
}

export interface LabelAssignAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "label_assign";
  preview: LabelAssetsPreview;
  status: ConfirmActionStatus;
}

export interface LabelUnassignAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "label_unassign";
  preview: LabelAssetsPreview;
  status: ConfirmActionStatus;
}

export interface LabelDeleteAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "label_delete";
  preview: LabelDeletePreview;
  status: ConfirmActionStatus;
}

export interface AssetUpdateAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "asset_update";
  preview: AssetUpdatePreview;
  status: ConfirmActionStatus;
}

export interface AssetDeleteAction {
  type: "confirm_action";
  actionId: string;
  actionKind: "asset_delete";
  preview: AssetDeletePreview;
  status: ConfirmActionStatus;
}

export type ActionConfigAction = ActionCreateAction | ActionUpdateAction;

type ChiefActionsAction = ActionConfigAction | ActionSetEnabledAction | ActionDeleteAction | ActionDryRunAction;

export type ChiefDeleteAction = ActionDeleteAction | LabelDeleteAction | AssetDeleteAction;

export type ChiefConfirmAction =
  | ChiefActionsAction
  | LabelCreateAction
  | LabelAssignAction
  | LabelUnassignAction
  | LabelDeleteAction
  | AssetUpdateAction
  | AssetDeleteAction;

export type ConfirmActionPayload =
  | SendEmailAction
  | CalendarAction
  | RsvpAction
  | MoveAction
  | GmailTrashAction
  | GmailDraftUpdateAction
  | GmailDraftDeleteAction
  | SlackConfirmAction
  | NotionConfirmAction
  | GDriveConfirmAction
  | LinearConfirmAction
  | GitHubConfirmAction
  | MCPCallAction
  | ChiefConfirmAction;

type GmailConfirmAction = SendEmailAction | GmailTrashAction | GmailDraftUpdateAction | GmailDraftDeleteAction;

// The Gmail writes, which every surface has to tell apart from the calendar
// kinds: those two families share the Google connection but nothing else, and a
// calendar-only control on a mail card would offer to notify attendees a mail
// has none of.
export const isGmailAction = (p: ConfirmActionPayload): p is GmailConfirmAction =>
  p.actionKind === "send_email" ||
  p.actionKind === "gmail_trash" ||
  p.actionKind === "gmail_draft_update" ||
  p.actionKind === "gmail_draft_delete";

export const isSlackAction = (p: ConfirmActionPayload): p is SlackConfirmAction =>
  p.actionKind === "send_message" ||
  p.actionKind === "send_dm" ||
  p.actionKind === "reply_thread" ||
  p.actionKind === "add_reaction";

export const isSlackTextAction = (p: ConfirmActionPayload): p is SlackTextAction =>
  isSlackAction(p) && p.actionKind !== "add_reaction";

export const isNotionAction = (p: ConfirmActionPayload): p is NotionConfirmAction =>
  p.actionKind === "notion_create_page" ||
  p.actionKind === "notion_update_page" ||
  p.actionKind === "notion_create_db_row" ||
  p.actionKind === "notion_update_db_row" ||
  p.actionKind === "notion_create_database" ||
  p.actionKind === "notion_add_comment" ||
  p.actionKind === "notion_update_database" ||
  p.actionKind === "notion_move_page";

export const isGDriveAction = (p: ConfirmActionPayload): p is GDriveConfirmAction =>
  p.actionKind === "gdrive_create_doc" || p.actionKind === "gdrive_update_doc";

export const isLinearAction = (p: ConfirmActionPayload): p is LinearConfirmAction =>
  p.actionKind === "linear_create_issue" ||
  p.actionKind === "linear_update_issue" ||
  p.actionKind === "linear_add_comment";

export const isGitHubAction = (p: ConfirmActionPayload): p is GitHubConfirmAction =>
  p.actionKind === "github_create_issue" ||
  p.actionKind === "github_update_issue" ||
  p.actionKind === "github_add_comment";

export const isMCPAction = (p: ConfirmActionPayload): p is MCPCallAction =>
  p.actionKind === "mcp_call_user" || p.actionKind === "mcp_call_org";

export const isChiefActionsAction = (p: ConfirmActionPayload): p is ChiefActionsAction =>
  p.actionKind === "action_create" ||
  p.actionKind === "action_update" ||
  p.actionKind === "action_set_enabled" ||
  p.actionKind === "action_delete" ||
  p.actionKind === "action_dry_run";

export const isChiefAction = (p: ConfirmActionPayload): p is ChiefConfirmAction =>
  isChiefActionsAction(p) ||
  p.actionKind === "label_create" ||
  p.actionKind === "smart_tag_create" ||
  p.actionKind === "label_assign" ||
  p.actionKind === "label_unassign" ||
  p.actionKind === "label_delete" ||
  p.actionKind === "asset_update" ||
  p.actionKind === "asset_delete";

export const isActionConfigAction = (p: ConfirmActionPayload): p is ActionConfigAction =>
  p.actionKind === "action_create" || p.actionKind === "action_update";

export const isChiefDeleteAction = (p: ConfirmActionPayload): p is ChiefDeleteAction =>
  p.actionKind === "action_delete" || p.actionKind === "label_delete" || p.actionKind === "asset_delete";

const isString = (v: unknown): v is string => typeof v === "string";
const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every(isString);
const isStringRecord = (v: unknown): v is Record<string, string> =>
  !!v && typeof v === "object" && !Array.isArray(v) && Object.values(v).every(isString);
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

const STATUSES: ReadonlySet<ConfirmActionStatus> = new Set([
  "pending",
  "queued",
  "executing",
  "executed",
  "rejected",
  "failed",
  "blocked",
]);
const isStatus = (v: unknown): v is ConfirmActionStatus =>
  typeof v === "string" && STATUSES.has(v as ConfirmActionStatus);

const CALENDAR_KINDS: ReadonlySet<CalendarAction["actionKind"]> = new Set([
  "calendar_create",
  "calendar_update",
  "calendar_delete",
]);

const parseEmailPreview = (v: unknown): EmailPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isStringArray(r.to)) return null;
  if (r.cc !== undefined && !isStringArray(r.cc)) return null;
  if (!isString(r.subject)) return null;
  if (!isString(r.body)) return null;
  if (r.draftId !== undefined && !isString(r.draftId)) return null;
  return {
    to: r.to,
    cc: r.cc,
    subject: r.subject,
    body: r.body,
    draftId: r.draftId,
  };
};

const parseCalendarPreview = (v: unknown): CalendarPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.title)) return null;
  if (r.start !== undefined && !isString(r.start)) return null;
  if (r.end !== undefined && !isString(r.end)) return null;
  if (r.allDay !== undefined && typeof r.allDay !== "boolean") return null;
  if (r.attendees !== undefined && !isStringArray(r.attendees)) return null;
  if (r.location !== undefined && !isString(r.location)) return null;
  if (r.description !== undefined && !isString(r.description)) return null;
  if (r.eventId !== undefined && !isString(r.eventId)) return null;
  if (r.addMeet !== undefined && typeof r.addMeet !== "boolean") return null;
  if (r.recurrence !== undefined && !isStringArray(r.recurrence)) return null;
  if (r.calendarId !== undefined && !isString(r.calendarId)) return null;
  return {
    title: r.title,
    start: r.start,
    end: r.end,
    allDay: r.allDay,
    attendees: r.attendees,
    location: r.location,
    description: r.description,
    eventId: r.eventId,
    addMeet: r.addMeet,
    recurrence: r.recurrence,
    calendarId: r.calendarId,
  };
};

const RSVP_RESPONSES: ReadonlySet<RsvpResponse> = new Set(["accepted", "declined", "tentative"]);
const isRsvpResponse = (v: unknown): v is RsvpResponse =>
  typeof v === "string" && RSVP_RESPONSES.has(v as RsvpResponse);

const parseRsvpPreview = (v: unknown): RsvpPreview | null => {
  const base = parseCalendarPreview(v);
  if (!base) return null;
  if (!base.eventId) return null;
  const r = v as Record<string, unknown>;
  if (!isRsvpResponse(r.response)) return null;
  return { ...base, eventId: base.eventId, response: r.response };
};

const parseMovePreview = (v: unknown): MovePreview | null => {
  const base = parseCalendarPreview(v);
  if (!base) return null;
  if (!base.eventId) return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.destinationCalendarId) || r.destinationCalendarId === "") return null;
  return { ...base, eventId: base.eventId, destinationCalendarId: r.destinationCalendarId };
};

const parseSlackSendMessagePreview = (v: unknown): SlackSendMessagePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.channelId)) return null;
  if (!isString(r.channelName)) return null;
  if (!isString(r.text)) return null;
  if (r.mentions !== undefined && !isStringRecord(r.mentions)) return null;
  return { channelId: r.channelId, channelName: r.channelName, text: r.text, mentions: r.mentions };
};

const parseSlackSendDmPreview = (v: unknown): SlackSendDmPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.recipientId)) return null;
  if (!isString(r.recipientName)) return null;
  if (!isString(r.text)) return null;
  if (r.mentions !== undefined && !isStringRecord(r.mentions)) return null;
  return { recipientId: r.recipientId, recipientName: r.recipientName, text: r.text, mentions: r.mentions };
};

const parseSlackReplyPreview = (v: unknown): SlackReplyPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.channelId)) return null;
  if (!isString(r.channelName)) return null;
  if (!isString(r.threadTs)) return null;
  if (!isString(r.text)) return null;
  if (r.mentions !== undefined && !isStringRecord(r.mentions)) return null;
  return {
    channelId: r.channelId,
    channelName: r.channelName,
    threadTs: r.threadTs,
    text: r.text,
    mentions: r.mentions,
  };
};

const parseSlackReactionPreview = (v: unknown): SlackReactionPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.channelId)) return null;
  if (!isString(r.channelName)) return null;
  if (!isString(r.messageTs)) return null;
  if (!isString(r.emoji)) return null;
  if (r.messageText !== undefined && !isString(r.messageText)) return null;
  return {
    channelId: r.channelId,
    channelName: r.channelName,
    messageTs: r.messageTs,
    emoji: r.emoji,
    messageText: r.messageText,
  };
};

const isGmailTrashMessage = (v: unknown): v is GmailTrashMessage => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.id) && isString(r.from) && isString(r.subject) && isString(r.date);
};

const parseGmailTrashPreview = (v: unknown): GmailTrashPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (typeof r.untrash !== "boolean") return null;
  if (!isStringArray(r.messageIds)) return null;
  if (!isNumber(r.count)) return null;
  const messages = Array.isArray(r.messages) && r.messages.every(isGmailTrashMessage) ? r.messages : [];
  return { untrash: r.untrash, messageIds: r.messageIds, count: r.count, messages };
};

const parseGmailDraftUpdatePreview = (v: unknown): GmailDraftUpdatePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.draftId)) return null;
  if (!isStringArray(r.to)) return null;
  if (!isStringArray(r.cc)) return null;
  if (!isStringArray(r.bcc)) return null;
  if (!isString(r.subject)) return null;
  if (!isString(r.body)) return null;
  if (!isString(r.currentSubject)) return null;
  if (!isStringArray(r.currentTo)) return null;
  return {
    draftId: r.draftId,
    to: r.to,
    cc: r.cc,
    bcc: r.bcc,
    subject: r.subject,
    body: r.body,
    currentSubject: r.currentSubject,
    currentTo: r.currentTo,
  };
};

const parseGmailDraftDeletePreview = (v: unknown): GmailDraftDeletePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.draftId)) return null;
  if (!isString(r.currentSubject)) return null;
  if (!isStringArray(r.currentTo)) return null;
  return { draftId: r.draftId, currentSubject: r.currentSubject, currentTo: r.currentTo };
};

const isNotionBlockSpec = (v: unknown): v is NotionBlockSpec => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.type) &&
    isString(r.text) &&
    (r.language === undefined || isString(r.language)) &&
    (r.checked === undefined || typeof r.checked === "boolean")
  );
};

const isNotionProperties = (v: unknown): v is Record<string, NotionPropertySpec> => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return Object.values(v as Record<string, unknown>).every((p) => {
    if (!p || typeof p !== "object") return false;
    return isString((p as Record<string, unknown>).type) && "value" in (p as Record<string, unknown>);
  });
};

const isNotionEditSpec = (v: unknown): v is NotionEditSpec => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return (
    isString(r.old_str) && isString(r.new_str) && (r.replace_all === undefined || typeof r.replace_all === "boolean")
  );
};

const parseNotionCreatePagePreview = (v: unknown): NotionCreatePagePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.parentPageId !== undefined && !isString(r.parentPageId)) return null;
  if (r.parentDatabaseId !== undefined && !isString(r.parentDatabaseId)) return null;
  if (r.title !== undefined && !isString(r.title)) return null;
  if (r.markdown !== undefined && !isString(r.markdown)) return null;
  if (r.blocks !== undefined && (!Array.isArray(r.blocks) || !r.blocks.every(isNotionBlockSpec))) return null;
  if (r.properties !== undefined && !isNotionProperties(r.properties)) return null;
  return {
    parentPageId: r.parentPageId,
    parentDatabaseId: r.parentDatabaseId,
    parentTitle: isString(r.parentTitle) ? r.parentTitle : undefined,
    title: r.title,
    iconEmoji: isString(r.iconEmoji) ? r.iconEmoji : undefined,
    markdown: r.markdown,
    blocks: r.blocks,
    properties: r.properties,
  };
};

const parseNotionUpdatePagePreview = (v: unknown): NotionUpdatePagePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.pageId)) return null;
  if (r.edits !== undefined && (!Array.isArray(r.edits) || !r.edits.every(isNotionEditSpec))) return null;
  if (r.replaceMarkdown !== undefined && !isString(r.replaceMarkdown)) return null;
  if (r.blocks !== undefined && (!Array.isArray(r.blocks) || !r.blocks.every(isNotionBlockSpec))) return null;
  if (r.position !== undefined && !isString(r.position)) return null;
  if (r.afterBlockId !== undefined && !isString(r.afterBlockId)) return null;
  if (r.archived !== undefined && typeof r.archived !== "boolean") return null;
  if (r.replaceContent !== undefined && typeof r.replaceContent !== "boolean") return null;
  if (r.allowDeletingContent !== undefined && typeof r.allowDeletingContent !== "boolean") return null;
  return {
    pageId: r.pageId,
    pageTitle: isString(r.pageTitle) ? r.pageTitle : undefined,
    edits: r.edits,
    replaceMarkdown: r.replaceMarkdown,
    blocks: r.blocks,
    position: r.position,
    afterBlockId: r.afterBlockId,
    archived: r.archived,
    replaceContent: r.replaceContent,
    allowDeletingContent: r.allowDeletingContent,
  };
};

const parseNotionCreateDbRowPreview = (v: unknown): NotionCreateDbRowPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.databaseId)) return null;
  if (!isNotionProperties(r.properties)) return null;
  return {
    databaseId: r.databaseId,
    databaseTitle: isString(r.databaseTitle) ? r.databaseTitle : undefined,
    properties: r.properties,
  };
};

const parseNotionUpdateDbRowPreview = (v: unknown): NotionUpdateDbRowPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.pageId)) return null;
  if (!isNotionProperties(r.properties)) return null;
  return {
    pageId: r.pageId,
    rowTitle: isString(r.rowTitle) ? r.rowTitle : undefined,
    properties: r.properties,
  };
};

const isNotionColumns = (v: unknown): v is Record<string, NotionDatabaseColumnSpec> => {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return Object.values(v as Record<string, unknown>).every((c) => {
    if (!c || typeof c !== "object") return false;
    const r = c as Record<string, unknown>;
    return (
      isString(r.type) &&
      (r.options === undefined || isStringArray(r.options)) &&
      (r.relation_data_source_id === undefined || isString(r.relation_data_source_id))
    );
  });
};

// Malformed rows are dropped rather than failing the whole card; each row must
// be an object of `{type, value}` cells.
const parseNotionRows = (v: unknown): Array<Record<string, NotionPropertySpec>> | undefined => {
  if (!Array.isArray(v)) return undefined;
  return v.every(isNotionProperties) ? v : undefined;
};

const parseNotionCreateDatabasePreview = (v: unknown): NotionCreateDatabasePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.parentPageId)) return null;
  if (!isString(r.title)) return null;
  if (!isNotionColumns(r.properties)) return null;
  return {
    parentPageId: r.parentPageId,
    parentTitle: isString(r.parentTitle) ? r.parentTitle : undefined,
    title: r.title,
    properties: r.properties,
    rows: parseNotionRows(r.rows),
  };
};

const parseNotionAddCommentPreview = (v: unknown): NotionAddCommentPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.pageId !== undefined && !isString(r.pageId)) return null;
  if (r.discussionId !== undefined && !isString(r.discussionId)) return null;
  if (!isString(r.text)) return null;
  return {
    pageId: r.pageId,
    discussionId: r.discussionId,
    pageTitle: isString(r.pageTitle) ? r.pageTitle : undefined,
    text: r.text,
  };
};

const parseNotionUpdateDatabasePreview = (v: unknown): NotionUpdateDatabasePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.databaseId)) return null;
  if (r.title !== undefined && !isString(r.title)) return null;
  if (r.addColumns !== undefined && !isNotionColumns(r.addColumns)) return null;
  if (r.removeColumns !== undefined && !isStringArray(r.removeColumns)) return null;
  return {
    databaseId: r.databaseId,
    databaseTitle: isString(r.databaseTitle) ? r.databaseTitle : undefined,
    dataSourceId: isString(r.dataSourceId) ? r.dataSourceId : undefined,
    title: r.title,
    addColumns: r.addColumns,
    removeColumns: r.removeColumns,
  };
};

const parseNotionMovePagePreview = (v: unknown): NotionMovePagePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.pageId)) return null;
  if (r.newParentPageId !== undefined && !isString(r.newParentPageId)) return null;
  if (r.newParentDataSourceId !== undefined && !isString(r.newParentDataSourceId)) return null;
  return {
    pageId: r.pageId,
    pageTitle: isString(r.pageTitle) ? r.pageTitle : undefined,
    newParentPageId: r.newParentPageId,
    newParentDataSourceId: r.newParentDataSourceId,
    parentTitle: isString(r.parentTitle) ? r.parentTitle : undefined,
  };
};

const parseGDriveCreateDocPreview = (v: unknown): GDriveCreateDocPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.title)) return null;
  return {
    title: r.title,
    content: isString(r.content) ? r.content : undefined,
    parentFolderId: isString(r.parentFolderId) ? r.parentFolderId : undefined,
  };
};

// The backend defaults an unset mode to replace before storing the proposal, so
// anything other than append reads as replace here too.
const parseGDriveUpdateDocPreview = (v: unknown): GDriveUpdateDocPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.fileId)) return null;
  return {
    fileId: r.fileId,
    fileTitle: isString(r.fileTitle) ? r.fileTitle : undefined,
    mode: r.mode === "append" ? "append" : "replace",
    content: isString(r.content) ? r.content : undefined,
  };
};

const parseLinearCreateIssuePreview = (v: unknown): LinearCreateIssuePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.teamName)) return null;
  if (!isString(r.title)) return null;
  if (r.labels !== undefined && !isStringArray(r.labels)) return null;
  return {
    teamName: r.teamName,
    title: r.title,
    description: isString(r.description) ? r.description : undefined,
    assignee: isString(r.assignee) ? r.assignee : undefined,
    priority: isNumber(r.priority) ? r.priority : undefined,
    project: isString(r.project) ? r.project : undefined,
    state: isString(r.state) ? r.state : undefined,
    labels: r.labels,
  };
};

// `description` keeps an empty string rather than collapsing it to undefined:
// the backend omits the field when the description is untouched, so an empty
// one means the write clears it.
const parseLinearUpdateIssuePreview = (v: unknown): LinearUpdateIssuePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.identifier)) return null;
  if (r.description !== undefined && !isString(r.description)) return null;
  if (r.addedLabels !== undefined && !isStringArray(r.addedLabels)) return null;
  if (r.removedLabels !== undefined && !isStringArray(r.removedLabels)) return null;
  return {
    identifier: r.identifier,
    issueTitle: isString(r.issueTitle) ? r.issueTitle : undefined,
    title: isString(r.title) ? r.title : undefined,
    description: r.description,
    assignee: isString(r.assignee) ? r.assignee : undefined,
    state: isString(r.state) ? r.state : undefined,
    priority: isNumber(r.priority) ? r.priority : undefined,
    project: isString(r.project) ? r.project : undefined,
    addedLabels: r.addedLabels,
    removedLabels: r.removedLabels,
  };
};

const parseLinearAddCommentPreview = (v: unknown): LinearAddCommentPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.identifier)) return null;
  if (!isString(r.body)) return null;
  return {
    identifier: r.identifier,
    issueTitle: isString(r.issueTitle) ? r.issueTitle : undefined,
    body: r.body,
  };
};

const parseGitHubCreateIssuePreview = (v: unknown): GitHubCreateIssuePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.repo)) return null;
  if (!isString(r.title)) return null;
  if (r.assignees !== undefined && !isStringArray(r.assignees)) return null;
  if (r.labels !== undefined && !isStringArray(r.labels)) return null;
  return {
    repo: r.repo,
    title: r.title,
    body: isString(r.body) ? r.body : undefined,
    assignees: r.assignees,
    labels: r.labels,
  };
};

const parseGitHubUpdateIssuePreview = (v: unknown): GitHubUpdateIssuePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.repo)) return null;
  if (!isNumber(r.number)) return null;
  if (!isString(r.reference)) return null;
  if (r.addLabels !== undefined && !isStringArray(r.addLabels)) return null;
  if (r.removeLabels !== undefined && !isStringArray(r.removeLabels)) return null;
  if (r.addAssignees !== undefined && !isStringArray(r.addAssignees)) return null;
  if (r.removeAssignees !== undefined && !isStringArray(r.removeAssignees)) return null;
  return {
    repo: r.repo,
    number: r.number,
    reference: r.reference,
    issueTitle: isString(r.issueTitle) ? r.issueTitle : undefined,
    addLabels: r.addLabels,
    removeLabels: r.removeLabels,
    addAssignees: r.addAssignees,
    removeAssignees: r.removeAssignees,
    state: isString(r.state) ? r.state : undefined,
    stateReason: isString(r.stateReason) ? r.stateReason : undefined,
  };
};

const parseGitHubAddCommentPreview = (v: unknown): GitHubAddCommentPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.repo)) return null;
  if (!isNumber(r.number)) return null;
  if (!isString(r.reference)) return null;
  if (!isString(r.body)) return null;
  return {
    repo: r.repo,
    number: r.number,
    reference: r.reference,
    issueTitle: isString(r.issueTitle) ? r.issueTitle : undefined,
    body: r.body,
  };
};

const parseMCPCallPreview = (v: unknown): MCPCallPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.serverId)) return null;
  if (!isString(r.serverName)) return null;
  if (!isString(r.toolName)) return null;
  // A Go nil map marshals as null, so an argument-less call arrives that way.
  if (r.args != null && (typeof r.args !== "object" || Array.isArray(r.args))) return null;
  return {
    serverId: r.serverId,
    serverName: r.serverName,
    toolName: r.toolName,
    args: (r.args as Record<string, unknown> | null | undefined) ?? {},
  };
};

const parseActionSchedulePreview = (v: unknown): ActionSchedulePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.hour)) return null;
  if (r.weekday !== undefined && !isString(r.weekday)) return null;
  if (r.monthDay !== undefined && !isString(r.monthDay)) return null;
  if (r.timezone !== undefined && !isString(r.timezone)) return null;
  if (r.minutes !== undefined && typeof r.minutes !== "number") return null;
  return {
    hour: r.hour,
    weekday: r.weekday,
    monthDay: r.monthDay,
    timezone: r.timezone,
    minutes: r.minutes,
  };
};

const TRIGGER_KINDS: ReadonlySet<ActionTriggerKind> = new Set(["new", "all"]);
const isTriggerKind = (v: unknown): v is ActionTriggerKind =>
  typeof v === "string" && TRIGGER_KINDS.has(v as ActionTriggerKind);

const parseActionTriggerPreview = (v: unknown): ActionTriggerPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isTriggerKind(r.kind)) return null;
  if (r.createdBy !== undefined && !isStringArray(r.createdBy)) return null;
  return { kind: r.kind, createdBy: r.createdBy };
};

const parseActionEmailOutcomePreview = (v: unknown): ActionEmailOutcomePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isStringArray(r.to)) return null;
  if (r.subject !== undefined && !isString(r.subject)) return null;
  if (r.includeDateInSubject !== undefined && typeof r.includeDateInSubject !== "boolean") return null;
  return {
    to: r.to,
    subject: r.subject,
    includeDateInSubject: r.includeDateInSubject,
  };
};

const parseActionScopePreview = (v: unknown): ActionScopePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.assetIds !== undefined && !isStringArray(r.assetIds)) return null;
  if (r.labelIds !== undefined && !isStringArray(r.labelIds)) return null;
  if (r.conceptIds !== undefined && !isStringArray(r.conceptIds)) return null;
  if (r.viewIds !== undefined && !isStringArray(r.viewIds)) return null;
  return {
    assetIds: r.assetIds,
    labelIds: r.labelIds,
    conceptIds: r.conceptIds,
    viewIds: r.viewIds,
  };
};

const parseActionConfigPreview = (v: unknown): ActionConfigPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.name)) return null;
  if (!isString(r.prompt)) return null;
  if (r.actionId !== undefined && !isString(r.actionId)) return null;
  if (r.description !== undefined && !isString(r.description)) return null;
  if (r.mode !== undefined && !isString(r.mode)) return null;
  if (r.enabled !== undefined && typeof r.enabled !== "boolean") return null;
  const schedule = r.schedule !== undefined ? parseActionSchedulePreview(r.schedule) : undefined;
  if (schedule === null) return null;
  const trigger = r.trigger !== undefined ? parseActionTriggerPreview(r.trigger) : undefined;
  if (trigger === null) return null;
  // The contract allows at most one run mechanism; both present is malformed.
  if (schedule && trigger) return null;
  const scope = r.scope !== undefined ? parseActionScopePreview(r.scope) : undefined;
  if (scope === null) return null;
  const email = r.email !== undefined ? parseActionEmailOutcomePreview(r.email) : undefined;
  if (email === null) return null;
  if (r.autoApprove !== undefined && !isStringArray(r.autoApprove)) return null;
  return {
    actionId: r.actionId,
    actionName: isString(r.actionName) ? r.actionName : undefined,
    name: r.name,
    description: r.description,
    prompt: r.prompt,
    schedule,
    trigger,
    scope,
    email,
    mode: r.mode,
    enabled: r.enabled,
    autoApprove: r.autoApprove,
  };
};

const parseActionSetEnabledPreview = (v: unknown): ActionSetEnabledPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.actionId)) return null;
  if (typeof r.enabled !== "boolean") return null;
  return {
    actionId: r.actionId,
    actionName: isString(r.actionName) ? r.actionName : undefined,
    enabled: r.enabled,
  };
};

const parseActionRefPreview = (v: unknown): ActionRefPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.actionId)) return null;
  return { actionId: r.actionId, actionName: isString(r.actionName) ? r.actionName : undefined };
};

const parseLabelCreatePreview = (v: unknown): LabelCreatePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  // smart_tag_create's params name the description `instruction`.
  const description = r.description ?? r.instruction;
  if (!isString(r.name)) return null;
  if (description !== undefined && !isString(description)) return null;
  if (r.icon !== undefined && !isString(r.icon)) return null;
  if (r.color !== undefined && !isString(r.color)) return null;
  return {
    name: r.name,
    description: description,
    icon: r.icon,
    color: r.color,
  };
};

// The name is dropped from the wire when the asset's display name couldn't be
// resolved; rejecting the ref would fail the whole card and park the turn.
const isLabelAssetRef = (v: unknown): v is LabelAssetRef => {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  return isString(r.id) && (r.name === undefined || isString(r.name));
};

const parseLabelAssetsPreview = (v: unknown): LabelAssetsPreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.labelId)) return null;
  if (!Array.isArray(r.assets) || !r.assets.every(isLabelAssetRef)) return null;
  return {
    labelId: r.labelId,
    labelName: isString(r.labelName) ? r.labelName : undefined,
    assets: r.assets,
  };
};

const parseLabelDeletePreview = (v: unknown): LabelDeletePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.labelId)) return null;
  return { labelId: r.labelId, labelName: isString(r.labelName) ? r.labelName : undefined };
};

const parseAssetUpdatePreview = (v: unknown): AssetUpdatePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.assetId)) return null;
  if (r.name !== undefined && !isString(r.name)) return null;
  if (r.description !== undefined && !isString(r.description)) return null;
  return {
    assetId: r.assetId,
    assetName: isString(r.assetName) ? r.assetName : undefined,
    name: r.name,
    description: r.description,
  };
};

const parseAssetDeletePreview = (v: unknown): AssetDeletePreview | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (!isString(r.assetId)) return null;
  return { assetId: r.assetId, assetName: isString(r.assetName) ? r.assetName : undefined };
};

// The preview shape is validated per `actionKind`; optional preview fields are tolerated.
export const parseConfirmActionPayload = (v: unknown): ConfirmActionPayload | null => {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (r.type !== "confirm_action") return null;
  if (!isString(r.actionId)) return null;
  if (!isStatus(r.status)) return null;

  if (r.actionKind === "send_email") {
    const preview = parseEmailPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "send_email",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "calendar_rsvp") {
    const preview = parseRsvpPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "calendar_rsvp",
      preview,
      notifyAttendeesDefault: r.notifyAttendeesDefault === true,
      status: r.status,
    };
  }

  if (r.actionKind === "calendar_move") {
    const preview = parseMovePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "calendar_move",
      preview,
      notifyAttendeesDefault: r.notifyAttendeesDefault === true,
      status: r.status,
    };
  }

  if (r.actionKind === "send_message") {
    const preview = parseSlackSendMessagePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "send_message",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "send_dm") {
    const preview = parseSlackSendDmPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "send_dm",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "reply_thread") {
    const preview = parseSlackReplyPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "reply_thread",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "add_reaction") {
    const preview = parseSlackReactionPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "add_reaction",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "gmail_trash") {
    const preview = parseGmailTrashPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "gmail_trash",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "gmail_draft_update") {
    const preview = parseGmailDraftUpdatePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "gmail_draft_update",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "gmail_draft_delete") {
    const preview = parseGmailDraftDeletePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "gmail_draft_delete",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_create_page") {
    const preview = parseNotionCreatePagePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_create_page",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_update_page") {
    const preview = parseNotionUpdatePagePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_update_page",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_create_db_row") {
    const preview = parseNotionCreateDbRowPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_create_db_row",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_update_db_row") {
    const preview = parseNotionUpdateDbRowPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_update_db_row",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_create_database") {
    const preview = parseNotionCreateDatabasePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_create_database",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_add_comment") {
    const preview = parseNotionAddCommentPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_add_comment",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_update_database") {
    const preview = parseNotionUpdateDatabasePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_update_database",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "notion_move_page") {
    const preview = parseNotionMovePagePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "notion_move_page",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "gdrive_create_doc") {
    const preview = parseGDriveCreateDocPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "gdrive_create_doc",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "gdrive_update_doc") {
    const preview = parseGDriveUpdateDocPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "gdrive_update_doc",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "action_create") {
    const preview = parseActionConfigPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "action_create",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "action_update") {
    const preview = parseActionConfigPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "action_update",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "action_set_enabled") {
    const preview = parseActionSetEnabledPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "action_set_enabled",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "action_delete") {
    const preview = parseActionRefPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "action_delete",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "action_dry_run") {
    const preview = parseActionRefPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "action_dry_run",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "label_create" || r.actionKind === "smart_tag_create") {
    const preview = parseLabelCreatePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: r.actionKind,
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "label_assign") {
    const preview = parseLabelAssetsPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "label_assign",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "label_unassign") {
    const preview = parseLabelAssetsPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "label_unassign",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "label_delete") {
    const preview = parseLabelDeletePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "label_delete",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "asset_update") {
    const preview = parseAssetUpdatePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "asset_update",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "asset_delete") {
    const preview = parseAssetDeletePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "asset_delete",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "linear_create_issue") {
    const preview = parseLinearCreateIssuePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "linear_create_issue",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "linear_update_issue") {
    const preview = parseLinearUpdateIssuePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "linear_update_issue",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "linear_add_comment") {
    const preview = parseLinearAddCommentPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "linear_add_comment",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "github_create_issue") {
    const preview = parseGitHubCreateIssuePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "github_create_issue",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "github_update_issue") {
    const preview = parseGitHubUpdateIssuePreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "github_update_issue",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "github_add_comment") {
    const preview = parseGitHubAddCommentPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: "github_add_comment",
      preview,
      status: r.status,
    };
  }

  if (r.actionKind === "mcp_call_user" || r.actionKind === "mcp_call_org") {
    const preview = parseMCPCallPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: r.actionKind,
      preview,
      status: r.status,
    };
  }

  if (typeof r.actionKind === "string" && CALENDAR_KINDS.has(r.actionKind as CalendarAction["actionKind"])) {
    const preview = parseCalendarPreview(r.preview);
    if (!preview) return null;
    return {
      type: "confirm_action",
      actionId: r.actionId,
      actionKind: r.actionKind as CalendarAction["actionKind"],
      preview,
      notifyAttendeesDefault: r.notifyAttendeesDefault === true,
      status: r.status,
    };
  }

  return null;
};
